/**
 * Global error handler for API errors and session expiration.
 * Provides a callback registration for React components to display notifications.
 */

let notificationCallback = null;
let lastNotificationTime = 0;
const NOTIFICATION_THROTTLE_MS = 3000; // Prevent duplicate notifications within 3 seconds

/**
 * Register a callback function to display notifications.
 * The callback should accept (message, type) parameters.
 * @param {Function} callback
 */
export const registerNotificationCallback = (callback) => {
  notificationCallback = callback;
};

/**
 * Show a notification using the registered callback.
 * Throttles duplicate notifications to avoid spamming.
 * @param {string} message
 * @param {string} type - 'success', 'error', 'info', 'warning'
 */
export const showNotification = (message, type = 'error') => {
  if (!notificationCallback) {
    console.warn('Notification callback not registered. Message:', message);
    return;
  }

  const now = Date.now();
  if (now - lastNotificationTime < NOTIFICATION_THROTTLE_MS) {
    return; // Skip to avoid duplicate notifications
  }

  lastNotificationTime = now;
  notificationCallback(message, type);
};

/**
 * Handle API errors:
 * - Network errors: API unreachable
 * - 401 Unauthorized: Session expired, redirect to login with notification
 * - 5xx Server errors: Show generic error message
 * - 4xx Client errors: Skip global notification (handled locally)
 * @param {Error} error - Axios error object
 */
export const handleApiError = (error) => {
  if (!error) return;

  const { config, response } = error;
  const isLoginRequest = config?.url?.includes('/auth/login');

  // Network error (no response)
  if (!response) {
    showNotification('Unable to connect to the server. Please check your internet connection.', 'error');
    return;
  }

  const { status, data } = response;

  // Session expired or invalid token (skip for login requests)
  if (status === 401 && !isLoginRequest) {
    // Clear local storage
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // Show notification
    showNotification('Your session has expired. Please log in again.', 'warning');

    // Redirect to login if not already there after a short delay to allow notification to be seen
    if (!window.location.pathname.includes('/login')) {
      setTimeout(() => {
        window.location.href = '/login';
      }, 800);
    }
    return;
  }

  // Skip global notification for login errors (handled locally)
  if (isLoginRequest) {
    return;
  }

  // Show notification for server errors (5xx)
  if (status >= 500) {
    const errorMessage = data?.error || data?.message || `Server error (${status})`;
    showNotification(errorMessage, 'error');
    return;
  }

  // For 4xx client errors (except 401), skip global notification
  // Let local error handlers show appropriate messages
  return;
};